import React, { useEffect, useState } from "react";
import { api, BACKEND_URL, TOKEN_KEY } from "@/lib/api";
import { Download, FileSpreadsheet, FileText, Package, Boxes as BoxesIcon, Layers } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const TICK = { fontSize: 10, fontFamily: "IBM Plex Mono" };
const TT = { fontSize: 12, fontFamily: "IBM Plex Mono", border: "1px solid #d4d4d8", borderRadius: 0 };

async function downloadFile(path, filename) {
  const token = localStorage.getItem(TOKEN_KEY);
  const resp = await fetch(`${BACKEND_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  const blob = await resp.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

function DownloadCard({ title, subtitle, icon: Icon, paths, testId }) {
  return (
    <div className="bg-white border border-zinc-300 p-5 flex flex-col" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Report</div>
          <h3 className="text-lg font-semibold mt-1">{title}</h3>
          <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{subtitle}</p>
        </div>
        <div className="h-9 w-9 bg-zinc-900 text-white flex items-center justify-center">
          <Icon size={16} />
        </div>
      </div>
      <div className="flex gap-2 mt-auto">
        <button
          data-testid={`${testId}-xlsx`}
          onClick={() => downloadFile(paths.xlsx, paths.xlsxName)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs uppercase tracking-wider font-mono"
        >
          <FileSpreadsheet size={14} /> Excel
        </button>
        <button
          data-testid={`${testId}-pdf`}
          onClick={() => downloadFile(paths.pdf, paths.pdfName)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white text-xs uppercase tracking-wider font-mono"
        >
          <FileText size={14} /> PDF
        </button>
      </div>
    </div>
  );
}

export default function Reports() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [byDay, setByDay] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCat, setSelectedCat] = useState("");

  const load = async () => {
    const params = { date_from: from, date_to: to };
    const [d, c, p] = await Promise.all([
      api.get("/reports/cartons-by-day", { params }),
      api.get("/reports/cartons-by-category", { params }),
      api.get("/products"),
    ]);
    setByDay(d.data); setByCategory(c.data); setProducts(p.data);
    if (!selectedCat && p.data.length) setSelectedCat(p.data[0].id);
  };
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [from, to]);

  const totals = byDay.reduce((s, d) => ({ cartons: s.cartons + d.cartons, net: s.net + d.net_kg, gross: s.gross + d.gross_kg }), { cartons: 0, net: 0, gross: 0 });
  const selectedProduct = products.find((p) => p.id === selectedCat);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Analytics & Exports</div>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Reports</h1>
      </div>

      {/* Download Center */}
      <div className="bg-white border border-zinc-300 p-5">
        <div className="flex items-center gap-2 mb-4 border-b border-zinc-200 pb-3">
          <Download size={16} className="text-orange-600" />
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Download Center</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DownloadCard
            testId="rpt-finished"
            title="All Finished Goods"
            subtitle="Every category with carton count, total net & gross weight. Totals row at the end."
            icon={BoxesIcon}
            paths={{
              xlsx: "/api/reports/finished-goods.xlsx", xlsxName: `finished-goods-${today}.xlsx`,
              pdf: "/api/reports/finished-goods.pdf", pdfName: `finished-goods-${today}.pdf`,
            }}
          />
          <DownloadCard
            testId="rpt-raw"
            title="All Raw Material"
            subtitle="Inward inventory: granules, additives, paper tubes, cartons with live stock & weights."
            icon={Package}
            paths={{
              xlsx: "/api/reports/raw-materials.xlsx", xlsxName: `raw-materials-${today}.xlsx`,
              pdf: "/api/reports/raw-materials.pdf", pdfName: `raw-materials-${today}.pdf`,
            }}
          />
          <div className="bg-white border border-zinc-300 p-5 flex flex-col" data-testid="rpt-category">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Report</div>
                <h3 className="text-lg font-semibold mt-1">Item-wise Finished Goods</h3>
                <p className="text-xs text-zinc-600 mt-1">Pick a category, get all its cartons with totals below.</p>
              </div>
              <div className="h-9 w-9 bg-zinc-900 text-white flex items-center justify-center"><Layers size={16} /></div>
            </div>
            <select
              data-testid="rpt-cat-select"
              value={selectedCat}
              onChange={(e) => setSelectedCat(e.target.value)}
              className="w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono mb-2"
            >
              {products.length === 0 ? <option value="">-- no categories --</option> : null}
              {products.map((p) => <option key={p.id} value={p.id}>{p.sku}</option>)}
            </select>
            <div className="flex gap-2 mt-auto">
              <button
                data-testid="rpt-cat-xlsx"
                disabled={!selectedCat}
                onClick={() => downloadFile(`/api/reports/category-cartons.xlsx?product_id=${selectedCat}`, `cartons-${selectedProduct?.sku || "category"}.xlsx`)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs uppercase tracking-wider font-mono disabled:opacity-50"
              ><FileSpreadsheet size={14} /> Excel</button>
              <button
                data-testid="rpt-cat-pdf"
                disabled={!selectedCat}
                onClick={() => downloadFile(`/api/reports/category-cartons.pdf?product_id=${selectedCat}`, `cartons-${selectedProduct?.sku || "category"}.pdf`)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white text-xs uppercase tracking-wider font-mono disabled:opacity-50"
              ><FileText size={14} /> PDF</button>
            </div>
          </div>
        </div>
      </div>

      {/* Date range for charts */}
      <div className="bg-white border border-zinc-300 p-4 flex gap-3 items-end flex-wrap">
        <div>
          <label className="text-[10px] font-semibold uppercase mb-1 block">From</label>
          <input data-testid="rep-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 px-2 text-sm border border-zinc-300 bg-white font-mono" />
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase mb-1 block">To</label>
          <input data-testid="rep-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 px-2 text-sm border border-zinc-300 bg-white font-mono" />
        </div>
        {[{ label: "Last 7d", days: 7 }, { label: "Last 30d", days: 30 }, { label: "Last 90d", days: 90 }].map((p) => (
          <button key={p.label} onClick={() => {
            const t = new Date().toISOString().slice(0,10);
            const f = new Date(Date.now() - p.days * 86400000).toISOString().slice(0,10);
            setFrom(f); setTo(t);
          }} className="h-9 px-3 text-[11px] uppercase font-mono border border-zinc-300 bg-white hover:bg-zinc-100">{p.label}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiBox label="Total Cartons" value={totals.cartons} />
        <KpiBox label="Total Net (kg)" value={totals.net.toFixed(2)} />
        <KpiBox label="Total Gross (kg)" value={totals.gross.toFixed(2)} color="text-orange-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-300 p-5">
          <div className="mb-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Trend</div>
            <h3 className="text-lg font-semibold">Cartons / kg by Day</h3>
          </div>
          <div className="h-64">
            {byDay.length === 0 ? <Empty /> : (
              <ResponsiveContainer>
                <LineChart data={byDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="date" tick={TICK} />
                  <YAxis tick={TICK} />
                  <Tooltip contentStyle={TT} />
                  <Line type="monotone" dataKey="net_kg" stroke="#ea580c" strokeWidth={2} dot={{ fill: "#ea580c", r: 3 }} name="Net kg" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="bg-white border border-zinc-300 p-5">
          <div className="mb-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Output</div>
            <h3 className="text-lg font-semibold">By Category</h3>
          </div>
          <div className="h-64">
            {byCategory.length === 0 ? <Empty /> : (
              <ResponsiveContainer>
                <BarChart data={byCategory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="sku" tick={TICK} />
                  <YAxis tick={TICK} />
                  <Tooltip contentStyle={TT} />
                  <Bar dataKey="net_kg" fill="#ea580c" name="Net kg" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Empty() { return <div className="text-sm text-zinc-500 h-full flex items-center justify-center border border-dashed border-zinc-300">No data in this range</div>; }
function KpiBox({ label, value, color = "text-zinc-900" }) {
  return (
    <div className="bg-white border border-zinc-300 p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono text-3xl font-semibold mt-2 ${color}`}>{value}</div>
    </div>
  );
}
